/**
 * Türkçe ondalık giriş — sunucudaki `core/money.ts` ile AYNI kuralları uygular.
 *
 * Eski `procurement_post.php` `bind_param`'da `'i'` kullanıyordu; girilen
 * 12,50 TL veritabanına 12 olarak yazılıyordu. Arayüz tarafındaki panzehir budur:
 * kullanıcı virgül yazar, biz kuruşa çeviririz, kırpma olmaz.
 *
 * ⚠ Sunucudaki kuralla aynı olmak ZORUNDA:
 *   · tek virgül HER ZAMAN ondalıktır (reçete adetleri meşru biçimde küsuratlı:
 *     0,125 m² masif panel — binlik varsaymak 1000 kat şişirirdi)
 *   · birden fazla virgül binlik ayracıdır
 *   · iki ayraç birlikte varsa sondaki ondalıktır
 */

/** Serbest metinden sayı. Bozuk girdide null döner (0 DEĞİL — fark önemli). */
export function parseDecimal(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;

  let s = v.trim().replace(/[^\d,.\-]/g, "");
  if (!s || s === "-") return null;

  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");

  if (lastComma > -1 && lastDot > -1) {
    s = lastComma > lastDot
      ? s.replace(/\./g, "").replace(",", ".")
      : s.replace(/,/g, "");
  } else if (lastComma > -1) {
    const commaCount = (s.match(/,/g) ?? []).length;
    s = commaCount > 1 ? s.replace(/,/g, "") : s.replace(",", ".");
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Sayıyı düzenlenebilir Türkçe metne çevirir. null/undefined → boş metin. */
export function formatDecimalInput(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "";
  return String(v).replace(".", ",");
}

/**
 * TL metnini kuruşa çevirir. Sunucudaki `toKurus` ile aynı yuvarlama.
 *
 * ── ★ PARADA NOKTA BİNLİK AYRACIDIR ────────────────────────────────────────
 * `parseDecimal` genel amaçlıdır ve tek noktayı ONDALIK sayar; reçete için bu
 * doğrudur (0.125 m² masif panel meşru bir miktardır). PARA için YANLIŞTIR:
 * Türkçe'de 670.000 "altı yüz yetmiş bin" demektir.
 *
 * Canlıda ölçüldü (2026-08-21): Finans ekranında maaş kalemine "670.000"
 * yazıldığında `parseDecimal` bunu 670 okuyor, kayda 670 TL geçiyordu — hata
 * vermeden, tam 1000 kat küçük. Kâr tablosu o kadar şişmiş görünürdü.
 *
 * KURAL: virgül yoksa ve HER noktadan sonra tam üç rakam varsa, noktalar
 * binlik ayracıdır. Baştaki grup "0" ise ondalık sayılır — "0.125" para
 * bağlamında bile 125 TL'ye dönüşmemeli.
 */
export function parseKurus(v: unknown): number | null {
  if (typeof v === "string") {
    const t = v.trim().replace(/[^\d,.\-]/g, "");
    const neg = t.startsWith("-");
    const body = neg ? t.slice(1) : t;
    // 1.250  ·  1.250.000  →  binlik;   12.50 · 0.125  →  ondalık
    if (/^\d{1,3}(\.\d{3})+$/.test(body) && !body.startsWith("0")) {
      const n = Number(body.replace(/\./g, ""));
      if (Number.isFinite(n)) return (neg ? -1 : 1) * Math.round(n * 100);
    }
  }
  const n = parseDecimal(v);
  if (n === null) return null;
  const scaled = n * 100;
  // Epsilon düzeltmesi: 1.005 * 100 = 100.49999999999999 → yuvarlama aşağı kaçar.
  const corrected = scaled + Math.sign(scaled) * Math.abs(scaled) * Number.EPSILON;
  return Math.sign(corrected) * Math.round(Math.abs(corrected));
}

/** Kuruşu düzenlenebilir TL metnine çevirir ("2169000" → "21690,00"). */
export function formatKurusInput(kurus: number | null | undefined): string {
  if (kurus === null || kurus === undefined || !Number.isFinite(kurus)) return "";
  return (kurus / 100).toFixed(2).replace(".", ",");
}

/** Girdi alanında anlık olarak izin verilen karakterler (yazarken engellemez). */
export function sanitizeDecimalKeystrokes(s: string): string {
  return s.replace(/[^\d.,\-]/g, "");
}
